/** @jsx isolateComponent */

import { useState } from 'react';

import { isolateComponent } from '@zapier/common-utils';
import { ensureDate } from '@zapier/date';

import { UpgradeModal } from '@zapier/design-system-revenue/src/components/offers';

import {
  useAccountSwitch,
  useTaskLimitUpgradeNudges,
} from '@zapier/design-system-revenue/src/hooks';

import { useBillingPeriod } from './useBillingPeriod';

const HAS_SEEN_OUT_OF_TASKS_MODAL = 'hasSeenOutOfTasksModal';
const TASK_LIMIT_80_PERCENT_MODAL_LAST_SEEN = 'taskLimit80PercentModalLastSeen';

export const setHasSeenOutOfTasksModal = (value: boolean) =>
  localStorage.setItem(HAS_SEEN_OUT_OF_TASKS_MODAL, value.toString());

const setSawTaskLimit80PercentModal = () =>
  localStorage.setItem(
    TASK_LIMIT_80_PERCENT_MODAL_LAST_SEEN,
    new Date().toString()
  );

type StatefulProps = {
  isEligibleFor80PercentTaskUpgradeNudge: boolean;
  shouldShowModal: boolean;
};

const LoginTaskLimitModalStateful = ({
  isEligibleFor80PercentTaskUpgradeNudge,
  shouldShowModal,
}: StatefulProps) => {
  const [isModalVisible, setIsModalVisible] = useState(shouldShowModal);

  const onClose = () => {
    setIsModalVisible(false);
  };

  if (!isModalVisible) {
    return null;
  }

  if (isEligibleFor80PercentTaskUpgradeNudge) {
    setSawTaskLimit80PercentModal();
  } else {
    setHasSeenOutOfTasksModal(true);
  }

  return (
    <UpgradeModal
      onClose={onClose}
      validationReason={
        isEligibleFor80PercentTaskUpgradeNudge ? 'tasks-80-percent' : 'tasks'
      }
    />
  );
};

export const LoginTaskLimitModal = () => {
  const {
    isLoading: isLoadingSwitch,
    isSwitchOn: isTaskLimitLoginModalSwitchOn,
  } = useAccountSwitch('revenueTaskLimitLoginModal');

  const {
    isLoading: isLoadingUpgradeNudges,
    isEligibleFor80PercentTaskUpgradeNudge,
    isEligibleFor100PercentTaskUpgradeNudge,
  } = useTaskLimitUpgradeNudges();

  const {
    isLoading: isLoadingBillingPeriod,
    periodEndDate,
    periodStartDate,
  } = useBillingPeriod();

  const isLoading =
    isLoadingSwitch || isLoadingUpgradeNudges || isLoadingBillingPeriod;

  const lastSaw80PercentTaskLimitModalString = localStorage.getItem(
    TASK_LIMIT_80_PERCENT_MODAL_LAST_SEEN
  );

  const lastSaw80PercentTaskLimitModal = lastSaw80PercentTaskLimitModalString
    ? ensureDate(lastSaw80PercentTaskLimitModalString)
    : undefined;

  const hasSeen80PercentModalThisBillingPeriod = Boolean(
    lastSaw80PercentTaskLimitModal &&
      periodStartDate &&
      periodEndDate &&
      lastSaw80PercentTaskLimitModal.getTime() >= periodStartDate.getTime() &&
      lastSaw80PercentTaskLimitModal.getTime() <= periodEndDate.getTime()
  );

  const shouldShowModal = Boolean(
    isEligibleFor80PercentTaskUpgradeNudge
      ? !hasSeen80PercentModalThisBillingPeriod
      : isEligibleFor100PercentTaskUpgradeNudge
      ? (localStorage.getItem(HAS_SEEN_OUT_OF_TASKS_MODAL) ?? 'false') ===
        'false'
      : false
  );

  return isLoading || !isTaskLimitLoginModalSwitchOn ? null : (
    <LoginTaskLimitModalStateful
      isEligibleFor80PercentTaskUpgradeNudge={
        isEligibleFor80PercentTaskUpgradeNudge
      }
      shouldShowModal={shouldShowModal}
    />
  );
};
